"use client";

import { cn } from "@/lib/utils";
import {
  getLibraryStyles,
  formatLibraryName,
} from "@/lib/colors/library-colors";
import {
  ComponentBooleanIcon,
  ComponentInstanceIcon,
  ComponentNoneIcon,
  Crosshair2Icon,
  DotFilledIcon,
  DotIcon,
} from "@radix-ui/react-icons";
import { memo } from "react";

interface FilterChipProps {
  label: string;
  isActive: boolean;
  isAvailable: boolean;
  isExclusive: boolean;
  onToggle: () => void;
  onExclusive: () => void;
  compact?: boolean;
  isLibrary?: boolean;
}

// Create a base FilterChip component
const FilterChipBase = ({
  label,
  isActive,
  isAvailable,
  isExclusive,
  onToggle,
  onExclusive,
  compact = false,
  isLibrary = false,
}: FilterChipProps) => {
  const libraryStyles = isLibrary ? getLibraryStyles(label) : undefined;

  // Format display label - use formatLibraryName for libraries
  const displayLabel = isLibrary ? formatLibraryName(label) : label;

  return (
    <div
      className={cn(
        "filter-chip flex items-center rounded-full border shadow-sm transition-all ease-in-out hover:-translate-y-1 active:scale-95",
        isActive
          ? "active-chip bg-primary text-primary-foreground border-transparent"
          : "inactive-chip bg-background border-border hover:border-primary/30 hover:bg-accent/30",
        isExclusive && "exclusive-chip ring-2 ring-primary/30 ring-offset-1",
        compact && "scale-90 h-7",
        isAvailable || isActive ? "opacity-100" : "opacity-60"
      )}
      style={{
        transform: "scale(1)",
        ...(isLibrary && { borderColor: libraryStyles?.borderColor }),
      }}
    >
      <button
        type="button"
        onClick={onToggle}
        onKeyDown={(e) => {
          if (e.key === "Enter" || e.key === " ") {
            onToggle();
          }
        }}
        className={cn(
          "px-3 py-1 font-medium capitalize rounded-l-full flex-grow transition-all ease-in-out active:scale-95 whitespace-nowrap overflow-hidden text-ellipsis",
          compact ? "text-xs h-7" : "text-sm h-8"
        )}
      >
        {displayLabel}
      </button>
      {!compact && (
        <>
          <div className="h-4 mx-1 border-l border-current opacity-30 transition-colors duration-300" />
          {isExclusive ? (
            <button
              type="button"
              onClick={onToggle}
              className="pr-2 pl-1 h-8 rounded-r-full transition-all ease-in-out active:scale-90"
              title={`Show only ${displayLabel}`}
            >
              <span className="sr-only">Show only {displayLabel}</span>
              <DotIcon className="w-4 h-4 transition-all duration-300" />
            </button>
          ) : (
            <button
              type="button"
              onClick={onExclusive}
              className="pr-2 pl-1 h-8 rounded-r-full transition-all ease-in-out active:scale-90"
              title={`Show only ${displayLabel}`}
            >
              <span className="sr-only">Show only {displayLabel}</span>
              <DotFilledIcon className="w-4 h-4 transition-all duration-300" />
            </button>
          )}
        </>
      )}
    </div>
  );
};

// Custom comparison function to prevent unnecessary re-renders
const areEqual = (prevProps: FilterChipProps, nextProps: FilterChipProps) => {
  return (
    prevProps.label === nextProps.label &&
    prevProps.isActive === nextProps.isActive &&
    prevProps.isAvailable === nextProps.isAvailable &&
    prevProps.isExclusive === nextProps.isExclusive &&
    prevProps.compact === nextProps.compact &&
    prevProps.isLibrary === nextProps.isLibrary
    // We intentionally omit the callback functions since they're unlikely to change identity
  );
};

// Export a memoized version of the FilterChip
export const FilterChip = memo(FilterChipBase, areEqual);
